import { Component, OnInit, NgZone, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'

declare const microsoftTeams: any

type ConsentStatus = 'LOADING' | 'READY' | 'SUBMITTING' | 'ERROR'

@Component({
  selector: 'app-teams-consent',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './teams-consent.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './teams-consent.component.scss',
})
export class TeamsConsentComponent implements OnInit {
  status: ConsentStatus = 'LOADING'
  errorMessage: string | null = null
  selectedOption: boolean | null = null

  constructor(
    private readonly ngZone: NgZone,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadTeamsSdk()
  }

  confirm() {
    if (this.selectedOption === null) {
      return
    }
    this.submitResponse(this.selectedOption)
  }

  cancel() {
    this.submitResponse(false)
  }

  private submitResponse(consented: boolean) {
    this.status = 'SUBMITTING'

    const callId = new URLSearchParams(window.location.search).get('callId')

    try {
      microsoftTeams.tasks.submitTask({ consented, callId })
    } catch {
      this.ngZone.run(() => {
        this.status = 'ERROR'
        this.errorMessage = this.translate.instant('TEAMS_CONSENT.ERROR_SUBMIT')
        this.changeDetector.detectChanges()
      })
    }
  }

  private loadTeamsSdk() {
    if (typeof microsoftTeams !== 'undefined') {
      this.initializeTeams()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://res.cdn.office.net/teams-js/2.24.0/js/MicrosoftTeams.min.js'
    script.onload = () => {
      this.initializeTeams()
    }
    script.onerror = () => {
      this.ngZone.run(() => {
        this.status = 'ERROR'
        this.errorMessage = this.translate.instant('TEAMS_CONSENT.ERROR_LOAD')
        this.changeDetector.detectChanges()
      })
    }
    document.head.appendChild(script)
  }

  private async initializeTeams() {
    try {
      const initTimeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Teams SDK timeout')), 5000))
      await Promise.race([microsoftTeams.app.initialize(), initTimeout])

      this.ngZone.run(() => {
        this.status = 'READY'
        this.changeDetector.detectChanges()
      })
    } catch {
      this.ngZone.run(() => {
        this.status = 'ERROR'
        this.errorMessage = this.translate.instant('TEAMS_CONSENT.ERROR_INIT')
        this.changeDetector.detectChanges()
      })
    }
  }
}
